// PowerSync createItem dispatch helper — extracted from
// capture-client-powersync.ts to keep that file under the 500-line
// gate. The contract:
//
//   1. Look up site_id + building_id from the local capture_sessions
//      row (both are denormalised on capture_items per the schema).
//   2. Derive a DETERMINISTIC id from the slot's natural key
//      (capture-item-id.ts) so a re-capture of the same slot is
//      idempotent (first-write-wins) instead of colliding server-side
//      and spinning up the re-home apparatus (H11/#659).
//   3. INSERT into capture_items with all the row fields. PowerSync's
//      SDK queues a PUT op CRUD entry; the connector POSTs that to
//      /capture/items with the same id (the backend's create-item.ts
//      honours the client-supplied id and resolves a same-id re-create
//      idempotently via tryIdempotentReturn — a 200, not a 409).
//   4. JOIN-readback to assemble the same CaptureItemRow DTO shape the
//      REST adapter returns. A same-slot re-create whose INSERT hit the
//      local PRIMARY KEY resolves to the existing row here (idempotent);
//      otherwise falls back to a synthesised DTO if the readback returns
//      nothing (defensive — same pattern as dispatchUpdateItem).

import { canonicalizeQuantity } from '../../hooks/powersync/quantity-canonical'

import type { CaptureEventBus } from './capture-events'
import { eventForResult } from './capture-client-events'
import {
  deriveCaptureItemId,
  MAX_FALLBACK_ID_GENERATIONS,
  mintFallbackCaptureItemId,
} from './capture-item-id'
import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'

/**
 * Minimum surface this dispatch needs from PowerSyncDb. Mirrored from
 * capture-client-powersync.ts so callers don't need to share the type.
 */
interface DispatchDb {
  execute(sql: string, params?: unknown[]): Promise<unknown>
  getAll<T = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ): Promise<T[]>
}

export async function dispatchCreateItem(
  mutation: Extract<CaptureMutation, { kind: 'createItem' }>,
  db: DispatchDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  // INSERT into capture_items locally. site_id and building_id come
  // from the session row; we look them up via getAll rather than
  // requiring the caller to supply them — same shape the REST
  // backend resolves server-side. captured_at + updated_at use
  // SQLite's now(); the Postgres trigger overwrites updated_at on
  // upload.
  const sessionRows = await db.getAll<{
    siteId: string
    buildingId: string | null
  }>(
    `SELECT site_id AS siteId, building_id AS buildingId
     FROM capture_sessions WHERE id = ? LIMIT 1`,
    [mutation.sessionId],
  )
  const session = sessionRows[0]
  if (session === undefined) {
    throw new Error(
      `dispatchCreateItem: capture_sessions row ${mutation.sessionId} not in local mirror — sync stream hasn't delivered it yet?`,
    )
  }

  // Deterministic id from the slot's natural key (capture-item-id.ts):
  // a re-capture of the SAME (session, space|site, asset) yields the SAME
  // id, so the second create is idempotent (first-write-wins) instead of
  // colliding server-side and triggering the re-home apparatus (H11/#659).
  // Derived after the session lookup because the site branch needs siteId.
  let id = await deriveCaptureItemId({
    captureSessionId: mutation.sessionId,
    spaceId: mutation.spaceId,
    siteId: session.siteId,
    assetTypeId: mutation.assetTypeId,
  })

  // Rejected-tombstone collision guard (audit-1 #1). An admin reject is a
  // SOFT delete: the row keeps its PRIMARY KEY but `rejected_at` is set, and
  // it is hidden from every read (the session/site read hooks filter
  // `rejected_at IS NULL`). A re-capture of that slot derives the SAME
  // deterministic id, so a re-INSERT would (a) collide on the local backing-
  // table PK — the readback would then surface the rejected tombstone as a
  // false "idempotent" success and SILENTLY SWALLOW the surveyor's count — and
  // (b) on upload, PK-collide on the server (the server's idempotency/scope
  // checks are `rejected_at IS NULL`-filtered, so they miss the tombstone and
  // run a raw INSERT → 23505 → 500 → device-queue wedge). The field only
  // re-adds a slot it sees as EMPTY, which means the tombstone has already
  // synced down (otherwise the old live row would still show it as present),
  // so the collision is locally detectable here. Mint a fresh fallback id so
  // the re-add becomes a NEW LIVE row coexisting with the tombstone — exactly
  // the pre-deterministic-id behaviour — preserving the admin's rejection and
  // the count. Because the client never sends the colliding id, the server
  // never hits the PK-collision path, so no server-side change is needed.
  // Walk fallback generations: the derived id may be a rejected tombstone; mint a
  // DETERMINISTIC fallback seeded by it (a re-dispatch mints the SAME id → idempotent
  // collision, no duplicate live row). If THAT fallback is ALSO a tombstone (the slot was
  // rejected → re-added → rejected again), mint the next generation, until the candidate is
  // NOT a tombstone — so a rejected slot stays re-addable across repeated reject/re-add
  // cycles (Codex). Bounded by MAX_FALLBACK_ID_GENERATIONS: a pathological reject history
  // can never loop forever (the last id just quarantines on upload — never a wedge).
  for (let gen = 0; gen < MAX_FALLBACK_ID_GENERATIONS; gen += 1) {
    const tombstone = await db.getAll<{ hit: number }>(
      `SELECT 1 AS hit FROM capture_items
       WHERE id = ? AND rejected_at IS NOT NULL LIMIT 1`,
      [id],
    )
    if (tombstone[0] === undefined) break
    id = await mintFallbackCaptureItemId(id)
  }

  // Idempotent local create. With a deterministic id, a same-slot
  // re-create (double-tap / offline retry) targets a row that already
  // exists locally, so the INSERT hits the capture_items PRIMARY KEY. ON
  // CONFLICT is unsupported on PowerSync views (lint:powersync-no-view-
  // upsert), so we cannot UPSERT — instead let the INSERT throw and confirm
  // via the readback below that the row is already present (idempotent
  // no-op, first-write-wins, matching the server's tryIdempotentReturn).
  // A successful INSERT takes the common path untouched; only a failure
  // with NO existing row is a genuine error and is rethrown.
  let insertError: unknown = null
  try {
    await db.execute(
      `INSERT INTO capture_items (
         id, capture_session_id, site_id, building_id, space_id,
         asset_type_id, quantity, is_zero_confirmed, notes,
         captured_at, updated_at, sync_version
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?,
                strftime('%Y-%m-%dT%H:%M:%fZ','now'),
                strftime('%Y-%m-%dT%H:%M:%fZ','now'),
                1)`,
      [
        id,
        mutation.sessionId,
        session.siteId,
        session.buildingId,
        mutation.spaceId,
        mutation.assetTypeId,
        // Canonicalise the committed draft ("5."/"") so a synthetic-row first
        // measurement never persists/queues a value the backend create POST
        // would 500 on ("" → numeric parse error) — audit-1 #1. See
        // quantity-canonical.ts.
        canonicalizeQuantity(mutation.quantity) ?? mutation.quantity,
        mutation.isZeroConfirmed ? 1 : 0,
        mutation.notes,
      ],
    )
  } catch (err) {
    insertError = err
  }

  // Read the row back through the same JOIN updateItem uses so the
  // resolved DTO carries assetTypeName / systemName / etc.
  const rows = await db.getAll<Record<string, unknown>>(
    `SELECT
       ci.id              AS id,
       ci.asset_type_id   AS assetTypeId,
       at.name            AS assetTypeName,
       at.unit_of_measure AS unitOfMeasure,
       at.grouping_convention AS groupingConvention,
       ass.id             AS systemId,
       ass.name           AS systemName,
       asub.id            AS subSystemId,
       asub.name          AS subSystemName,
       ci.quantity        AS quantity,
       ci.is_zero_confirmed AS isZeroConfirmedRaw,
       ci.notes           AS notes,
       ci.captured_at     AS capturedAt,
       ci.updated_at      AS updatedAt,
       ci.sync_version    AS syncVersion,
       at.is_field_added  AS isFieldAddedRaw,
       at.requires_review AS requiresReviewRaw
     FROM capture_items ci
     LEFT JOIN asset_types at       ON at.id   = ci.asset_type_id
     LEFT JOIN asset_sub_systems asub ON asub.id = at.sub_system_id
     LEFT JOIN asset_systems ass    ON ass.id  = asub.system_id
     WHERE ci.id = ? AND ci.rejected_at IS NULL
     LIMIT 1`,
    [id],
  )
  const row = rows[0]
  // INSERT failed AND no row exists ⇒ a genuine failure (not a same-slot
  // duplicate that the readback would have found). Surface it rather than
  // fabricating a synthesised DTO that would later fail to upload.
  if (insertError !== null && row === undefined) {
    throw insertError
  }
  const item =
    row === undefined
      ? {
          id,
          assetTypeId: mutation.assetTypeId,
          assetTypeName: '',
          unitOfMeasure: 'Count',
          groupingConvention: '',
          systemId: '',
          systemName: '',
          subSystemId: '',
          subSystemName: '',
          quantity: mutation.quantity,
          isZeroConfirmed: mutation.isZeroConfirmed,
          notes: mutation.notes,
          capturedAt: '',
          updatedAt: '',
          syncVersion: 1,
          isFieldAdded: false,
          requiresReview: false,
          isTemplateOnly: false,
        }
      : {
          id: row['id'],
          assetTypeId: row['assetTypeId'],
          assetTypeName: row['assetTypeName'],
          unitOfMeasure: row['unitOfMeasure'],
          groupingConvention: row['groupingConvention'],
          systemId: row['systemId'],
          systemName: row['systemName'],
          subSystemId: row['subSystemId'],
          subSystemName: row['subSystemName'],
          quantity: row['quantity'],
          isZeroConfirmed: row['isZeroConfirmedRaw'] === 1,
          notes: row['notes'],
          capturedAt: row['capturedAt'],
          updatedAt: row['updatedAt'],
          syncVersion: row['syncVersion'],
          isFieldAdded: row['isFieldAddedRaw'] === 1,
          requiresReview: row['requiresReviewRaw'] === 1,
          isTemplateOnly: false,
        }

  const result: CaptureMutationResult = { kind: 'createItem', item }
  bus.emit(eventForResult(mutation, result))
  return result
}
